import { useLazyQuery } from "@apollo/client";
import {
  BaseTable,
  Button,
  Flexbox,
  Icons,
  Label,
  Modal,
  Sentiments,
  SingleDatePicker,
  Text,
  TextInput,
  Variants
} from "@sede-x/shell-ds-react-framework";
import { endOfMonth, startOfMonth } from "date-fns";
import dayjs, { Dayjs } from "dayjs";
import { loader } from "graphql.macro";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import InlineLoadingPanel from "shared/components/InlineLoadingPanel";
import { GqlResponse, TAllocationMovement, TMovement } from "ticketing/ticketing.types";
import {
  equalsIgnoreCase,
  formatNumber,
  fromISODateString,
  toMovementFilterInputFromMovement,
  truncateTo
} from "ticketing/utils";

const MOVEMENTS_SEARCH = loader("../../ticketing-graphql/movementSearch.graphql");

type MovementSearchResponse = GqlResponse<TMovement[], "movementsFilterBy">;

type VolumeAllocationContextProps = {
  totalQuantityToAllocate: number;
  leftOverQuantityToAllocate: number;
};

const VolumeAllocationContext = createContext<VolumeAllocationContextProps>({
  totalQuantityToAllocate: 0,
  leftOverQuantityToAllocate: 0
});

const FinalQuantityCell1 = ({ row }: { row: { original: TAllocationMovement } }) => {
  const { finalScheduledQuantity, scheduledQuantity } = row.original;

  return (
    <span
      style={{
        color: finalScheduledQuantity !== scheduledQuantity ? "orange" : "#e7e7e7"
      }}>
      {finalScheduledQuantity}
    </span>
  );
};
const activeRowRef: { id: string | null } = { id: null };
const NumericCell1 = ({
  row,
  column,
  onChange
}: {
  row: { original: TAllocationMovement };
  column: { id: string };
  onChange: (newValue: number) => void;
}) => {
  const { totalQuantityToAllocate } = useContext(VolumeAllocationContext);
  const rowId = row.original.id || `row-${Math.random()}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const isInEdit = row.original.inEdit; // Check if the row is in edit mode
  const field = column.id; // Get the field name from the column
  const value = row.original[field as keyof TAllocationMovement] || 0; // Get the value or default to 0
  const [newValue, setNewValue] = useState(value);
  const maxQuantity =
    totalQuantityToAllocate < 0
      ? Math.abs(row.original.scheduledQuantity - (row.original.actualizedQuantity ?? 0))
      : undefined;
  
  
  useEffect(() => {
    if (isInEdit && inputRef.current && (!activeRowRef.id || activeRowRef.id === rowId)) {
      inputRef.current.focus();
      activeRowRef.id = rowId;
    }
  }, [isInEdit, rowId]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    activeRowRef.id = rowId;
    const numericValue = Number(event.target.value);
    setNewValue(numericValue);
    onChange(numericValue);
  };

  return (
    <>
      {isInEdit ? (
        <div>
          <TextInput
            ref={inputRef}
            value={
              typeof newValue === "boolean" || newValue instanceof Date
                ? String(newValue)
                : newValue
            }
            onChange={handleChange}
            onFocus={() => {
              activeRowRef.id = rowId;
            }}
            min={0}
            max={maxQuantity}
            key={`input-${row.original.id}-${field}`}
            step={1}
          />
        </div>
      ) : (
        <span>{value instanceof Date ? value.toLocaleString() : value}</span>
      )}
    </>
  );
};
const isStatusExpectedOrScheduled = (status?: string) =>
  equalsIgnoreCase(status, "expected") || equalsIgnoreCase(status, "scheduled");

const transformMovements = (
  source: TMovement,
  quantityToAllocate: number,
  movements: TMovement[]
): TAllocationMovement[] => {
  //filter out the source movements, and actualized - internal and external
  return movements
    ?.filter(
      m =>
        source.id !== m.id &&
        source.activityType?.id === m.activityType?.id &&
        !m.isActualizedExternally &&
        isStatusExpectedOrScheduled(m.status?.name)
    )
    ?.filter(
      m => m.tickets?.length === 0 && (quantityToAllocate > 0 || m.scheduledQuantity !== 0)
    )
    ?.map(m => ({
      id: m.id,
      version: m.version,
      startDate: fromISODateString(m.startDate)!,
      endDate: fromISODateString(m.endDate)!,
      deliveryId: m.enterpriseSystemCode,
      scheduledQuantity: m.scheduledQuantity,
      actualizedQuantity: m.actualizedQuantity,
      finalScheduledQuantity: m.scheduledQuantity,
      quantityAllocated: 0,
      inEdit: true
    }))
    ?.sort((m1, m2) => m1.startDate.getTime() - m2.startDate.getTime());
};

/**
 *
 * @param param0
 * @returns
 */

const columns = (
  isIncrease: boolean,
  handleItemChange: (rowId: string, field: keyof TAllocationMovement, newValue: number) => void
) => [
  {
    header: isIncrease ? "Give" : "Take",
    accessorKey: "quantityAllocated",
    cell: (props: { row: { original: TAllocationMovement }; column: { id: string } }) => (
      <NumericCell1
        row={props.row}
        column={props.column}
        onChange={newValue =>
          handleItemChange(
            props.row.original.id,
            props.column.id as keyof TAllocationMovement,
            newValue
          )
        }
      />
    )
  },
  {
    header: "Delivery Id",
    accessorKey: "deliveryId"
  },
  {
    header: "Date",
    accessorKey: "startDate",
    cell: (props: { row: { original: TAllocationMovement } }) => (
      <span>{new Date(props.row.original.startDate).toLocaleDateString()}</span>
    )
  },
  {
    header: "Scheduled Quantity",
    accessorKey: "scheduledQuantity"
  },
  {
    header: "Final Quantity",
    accessorKey: "finalScheduledQuantity",
    cell: (props: { row: { original: TAllocationMovement } }) => (
      <FinalQuantityCell1 row={props.row} />
    )
  }
];

const VolumeAllocationModal = ({
  quantityToAllocate,
  onCancel,
  onSave,
  sourceMovement
}: {
  quantityToAllocate: number;
  onCancel: () => void;
  onSave: (allocations: TAllocationMovement[] | null) => void;
  sourceMovement: TMovement;
}) => {
  const isIncrease = quantityToAllocate > 0;
  const [volumeAllocationNomsSearch, { loading, error }] = useLazyQuery<MovementSearchResponse>(
    MOVEMENTS_SEARCH,
    {
      fetchPolicy: "no-cache",
      onCompleted: data =>
        setVolumeAllocationNoms(
          transformMovements(sourceMovement, quantityToAllocate, data.movementsFilterBy)
        )
    }
  );

  const [volumeAllocationNoms, setVolumeAllocationNoms] = useState<TAllocationMovement[]>([]);

  const [leftOverQuantityToAllocate, setLeftOverQuantityToAllocate] = useState<number>(
    Math.abs(quantityToAllocate)
  );

  const [movementStartDate, setMovementStartDate] = useState<Date>(startOfMonth(new Date()));
  const [movementEndDate, setMovementEndDate] = useState<Date>(endOfMonth(new Date()));

  const searchForMatchingMovements = useCallback(
    (startDate: Date, endDate: Date) => {
      //build filters from sourceMovement
      const movementsFilter = toMovementFilterInputFromMovement(
        sourceMovement,
        startDate,
        endDate
      );
      volumeAllocationNomsSearch({
        variables: { movementsFilter }
      });
    },
    [sourceMovement, volumeAllocationNomsSearch]
  );

  const [showConfirmModal, setShowConfirmModal] = useState({
    showModal: false,
    message: ""
  });

  useEffect(() => {
    const startDate = startOfMonth(new Date());
    const endDate = endOfMonth(new Date());
    searchForMatchingMovements(startDate, endDate);
  }, [searchForMatchingMovements]);

  const handleStartDateChange = (date: Dayjs | null) => {
    if (date) {
      setMovementStartDate(date.toDate());
    }
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    if (date) {
      setMovementEndDate(date.toDate());
    }
  };

  const handleSave = () => {
    const totalVolumeAllocated = truncateTo(
      volumeAllocationNoms.reduce((s, i) => s + i.quantityAllocated, 0),
      4
    );
    const actualQuantityToAllocate = Math.abs(quantityToAllocate);
    if (totalVolumeAllocated === actualQuantityToAllocate) {
      return doSave();
    }
    setShowConfirmModal({
      showModal: true,
      message: `You are about to ${
        totalVolumeAllocated > actualQuantityToAllocate ? "Over" : "Under"
      } allocate, would you like to proceed?`
    });
    return undefined;
  };

  const onConfirmAllocation = () => {
    setShowConfirmModal({ showModal: false, message: "" });

    doSave();
  };

  const doSave = () => {
    const allocations = volumeAllocationNoms
      .filter(v => v.quantityAllocated > 0)
      .map(a => ({
        ...a,
        quantityAllocated: a.quantityAllocated * (quantityToAllocate < 0 ? -1 : 1)
      }));
    onSave(allocations);
  };

  const handleItemChange = (
    rowId: string,
    field: keyof TAllocationMovement,
    newValue: number
  ) => {
    // Reset leftover quantity
    setLeftOverQuantityToAllocate(
      Math.abs(quantityToAllocate) -
        newValue -
        volumeAllocationNoms
          .filter(a => a.id !== rowId)
          .reduce((n, a) => n + a.quantityAllocated, 0)
    );

    // Update state with the new quantity
    setVolumeAllocationNoms(allocations =>
      allocations.map(a => {
        if (a.id === rowId) {
          return {
            ...a,
            [field]: newValue,
            finalScheduledQuantity: isIncrease
              ? a.scheduledQuantity + newValue
              : a.scheduledQuantity - newValue
          };
        }
        return a;
      })
    );
  };

  const allocationState = useMemo(
    () => ({
      totalQuantityToAllocate: quantityToAllocate,
      leftOverQuantityToAllocate
    }),
    [leftOverQuantityToAllocate, quantityToAllocate]
  );

  return (
    <Modal
      open={true}
      width={1000}
      height={300}
      bodyPadding={false}
      className="volume-allocation-modal"
      title={`Volume Allocation (${sourceMovement.enterpriseSystemCode})`}
      onClose={onCancel}
      actions={[
        {
          label: "Save without Allocating",
          action: () => {
            onSave(null);
          },
          props: {
            icon: <Icons.CaretSmallUpDown />
          }
        },

        {
          label: "Save",
          action: () => {
            handleSave();
          },
          props: {
            icon: <Icons.SaveAlt />,
            sentiment: Sentiments.Positive,
            disabled: leftOverQuantityToAllocate === Math.abs(quantityToAllocate)
          }
        },
        {
          label: "Cancel",
          action: () => {
            onCancel();
          },
          props: {
            sentiment: Sentiments.Negative,
            icon: <Icons.CrossCircle />
          }
        }
      ]}>
      <VolumeAllocationContext.Provider value={allocationState}>
        <div className={"content-wrapper"} style={{ height: "100%" }}>
          <div className={"grid-wrapper"} style={{ height: "100%" }}>
            <div className={"card-container"} style={{ height: "100%" }}>
              {loading && <InlineLoadingPanel />}
              {error && <ApolloErrorViewer error={error}></ApolloErrorViewer>}

              <Flexbox
                flexWrap="nowrap"
                alignItems="center"
                justifyContent="space-between"
                style={{
                  width: "100%",
                  padding: "8px"
                }}>
                <Flexbox alignItems="center" gap="4px" flex="0 0 auto">
                  <Text size="small">{`Quantity to Allocate:`}</Text>

                  <Text size="small">
                    {`${formatNumber(leftOverQuantityToAllocate)} / ${formatNumber(
                      quantityToAllocate
                    )} ${sourceMovement?.unitOfMeasure?.name || ""}`}
                  </Text>

                  <Text>{isIncrease ? <Icons.ArrowUp /> : <Icons.ArrowDown />}</Text>
                  <Text>{`${isIncrease ? "Increase" : "Decrease"}`}</Text>
                </Flexbox>

                <Flexbox gap="8px" alignItems="center" flex="0 0 auto">
                  <Label>From</Label>
                  <SingleDatePicker
                    placeholder="DD MMM YYYY"
                    value={dayjs(movementStartDate)}
                    onChange={handleStartDateChange}
                    size="small"
                    allowClear={false}
                  />
                  <Label>To</Label>
                  <SingleDatePicker
                    placeholder="DD MMM YYYY"
                    value={dayjs(movementEndDate)}
                    onChange={handleEndDateChange}
                    size="small"
                    allowClear={false}
                  />
                  <Button
                    size="small"
                    icon={<Icons.Search />}
                    onClick={() =>
                      searchForMatchingMovements(movementStartDate, movementEndDate)
                    }
                    style={{
                      minWidth: "90px",
                      gap: "4px"
                    }}
                    sentiment="negative">
                    Search
                  </Button>
                </Flexbox>
              </Flexbox>
              <Flexbox flex="1 1 auto" overflow="auto" className="table-container">
                <BaseTable
                  data={volumeAllocationNoms}
                  columns={columns(isIncrease, handleItemChange)}
                  tableOptions={{
                    enableColumnResizing: true,
                    columnResizeMode: "onChange"
                  }}
                  style={{ width: "100%", tableLayout: "fixed" }}
                />
              </Flexbox>
            </div>
          </div>
        </div>
        {showConfirmModal.showModal && (
          <Modal
            title={"Volume allocation confirmation"}
            open={true}
            onClose={() => setShowConfirmModal({ showModal: false, message: "" })}
            actions={[
              {
                label: "No",
                action: () => setShowConfirmModal({ showModal: false, message: "" }),
                props: {
                  icon: <Icons.CrossCircle />,
                  variant: Variants.Outlined
                }
              },
              {
                label: "Yes",
                action: onConfirmAllocation,
                props: {
                  icon: <Icons.Check />
                }
              }
            ]}>
            <Text>{showConfirmModal.message}</Text>
          </Modal>
        )}
      </VolumeAllocationContext.Provider>
    </Modal>
  );
};

export default VolumeAllocationModal;
